import i18n from '@/utils/i18n';
import React, { useCallback, useState } from 'react';
import { ActivityNoteReminderPicker } from './components/activity-note-reminder-picker/activity-note-reminder-picker';
import {
  Button,
  IconButton,
  Icons,
  Input,
  SidePanel,
  Typography,
} from '@usepepper/sprout-kit-ui';
import { DateTimeSelection } from '@/experiences/sales-portal/blocs/utils/date-picker-calendar-utils';
import { useCreateActivityNoteMutator } from '@/experiences/sales-portal/blocs/mutators/use-create-activity-note-mutator';
import { useCreateActivityReminderMutator } from '@/experiences/sales-portal/blocs/mutators/use-create-activity-reminder-mutator';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { useTheme } from '@/contexts/theme-context';

interface ActivityNotePanelProps {
  chatUUID: string;
  createdAt: string;
  onClose: () => void;
  onNoteSaved?: () => void;
}

export const ActivityNotePanel: React.FC<ActivityNotePanelProps> = ({
  chatUUID,
  createdAt,
  onClose,
  onNoteSaved,
}) => {
  const theme = useTheme();
  const { showErrorToast, showToast } = useExperienceToasts();

  const [noteContent, setNoteContent] = useState('');
  const [reminder, setReminder] = useState<DateTimeSelection | undefined>();

  const onSaveError = useCallback(() => {
    showErrorToast(i18n.t('sales_portal.activity_note.error'));
  }, [showErrorToast]);

  const onSaveSuccess = useCallback(() => {
    showToast({
      title: i18n.t('sales_portal.activity_note.success'),
      variant: 'info',
    });

    onNoteSaved?.();
    setNoteContent('');
    setReminder(undefined);

    onClose();
  }, [onClose, onNoteSaved, showToast]);

  const { createNote, loading: isSavingNote } = useCreateActivityNoteMutator({
    onError: onSaveError,
  });

  const onReminderError = useCallback(() => {
    showErrorToast(i18n.t('sales_portal.activity_note.reminder.error'));
  }, [showErrorToast]);

  const { createReminder, loading: isSavingReminder } =
    useCreateActivityReminderMutator({
      onError: onReminderError,
    });

  const handleSave = useCallback(async () => {
    if (!noteContent.trim()) {
      return;
    }

    // throw if reminder is or will be in the past
    if (reminder?.time && reminder.time <= new Date()) {
      showErrorToast(i18n.t('sales_portal.activity_note.reminder.past_error'));
      return;
    }

    const createdActivity = await createNote({
      chatUUID,
      noteContent: noteContent.trim(),
    });

    if (!createdActivity) {
      return;
    }

    if (reminder?.time) {
      await createReminder({
        chatUUID,
        crmActivityUUID: createdActivity.uuid,
        reminderDatetime: reminder.time,
      });
    }

    onSaveSuccess();
  }, [
    chatUUID,
    createNote,
    createReminder,
    noteContent,
    onSaveSuccess,
    reminder,
    showErrorToast,
  ]);

  const isSaving = isSavingNote || isSavingReminder;
  const isSaveDisabled = !noteContent.trim() || isSaving;

  return (
    <>
      <SidePanel.Header
        leadingActions={
          <IconButton
            aria-label={i18n.t('sales_portal.side_panel.close')}
            icon={<Icons.X color={theme.sk.color.icon.primary} />}
            onPress={onClose}
            shape="rounded"
            size="$sm"
            variant="tertiary"
          />
        }
        title={
          <Typography size="$xs" variant="heading">
            {i18n.t('sales_portal.activity_note.panel_title')}
          </Typography>
        }
      />
      <SidePanel.Body
        accessibilityLabel={i18n.t('sales_portal.activity_note.title')}
        gap={theme.sk.spacing.spaceSmall}
        role="dialog"
      >
        <Typography
          color={theme.sk.color.text.tertiary}
          size="$sm"
          variant="body"
        >
          {createdAt}
        </Typography>
        <ActivityNoteReminderPicker
          onSetReminder={setReminder}
          reminder={reminder}
        />
        <Input
          flex={1}
          multiline
          onChangeText={setNoteContent}
          placeholder={i18n.t('sales_portal.activity_note.note_placeholder')}
          size="$lg"
          value={noteContent}
          variant="ghost"
        />
      </SidePanel.Body>
      <SidePanel.Footer>
        <Button
          disabled={isSaveDisabled}
          loading={isSaving}
          onPress={handleSave}
          size="$md"
          variant="primary"
          width="100%"
        >
          {i18n.t('sales_portal.activity_note.save')}
        </Button>
      </SidePanel.Footer>
    </>
  );
};
